import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['item', 'nav'];
  static values = {
    activeClass: { type: String, default: 'text-brand-1000' },
  };

  connect() {
    this.sections = this._buildSections();

    this.observer = new IntersectionObserver(() => this._recomputeActive(), {
      root: null,
      rootMargin: '0px',
      threshold: [0, 0.01, 0.1, 0.25, 0.5, 0.75, 0.99, 1],
    });

    this.sections.forEach((s) => this.observer.observe(s.el));

    this._onRelayout = () => this._recomputeActive();
    window.addEventListener('resize', this._onRelayout, { passive: true });
    window.addEventListener('scroll', this._onRelayout, { passive: true });
    window.addEventListener('pointerdown', this._onClickOrFocus);
    window.addEventListener('focus', this._onClickOrFocus, true);

    this._recomputeActive();
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this._onRelayout);
    window.removeEventListener('scroll', this._onRelayout);
    window.removeEventListener('pointerdown', this._onClickOrFocus);
    window.removeEventListener('focus', this._onClickOrFocus);
  }

  scrollTo(event) {
    const id = event.currentTarget.dataset.scrollToSectionIdParam;
    // Find the first visible section with this ID
    const allElements = document.querySelectorAll(`[id="${id}"]`);
    const section = Array.from(allElements).find((el) => this._isElementVisible(el)) || document.getElementById(id);

    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this._flashAfterScroll(section);
      this._setActive(id);
    }
  }

  _recomputeActive() {
    // Dynamically find visible sections for each nav button
    const visibleSections = this._getVisibleSections();
    if (!visibleSections.length) return;

    const activeElement = document.activeElement;
    if (activeElement) {
      const focusedSection = visibleSections.find((s) => s.el && s.el.contains(activeElement));
      if (focusedSection) {
        return;
      }
    }

    const vh = window.innerHeight || document.documentElement.clientHeight;
    const atBottom = this._isAtBottomWithSections(visibleSections);

    if (atBottom) {
      const last = visibleSections[visibleSections.length - 1];
      if (last) this._setActive(last.id);
      return;
    }

    const metrics = visibleSections
      .map((s) => {
        const r = s.el.getBoundingClientRect();
        const fully = r.top >= 0 && r.bottom <= vh;
        const intersecting = r.bottom > 0 && r.top < vh;
        return { ...s, rect: r, fully, intersecting };
      });

    const fullyVisible = metrics.filter((m) => m.fully).sort((a, b) => a.rect.top - b.rect.top);
    if (fullyVisible.length) {
      this._setActive(fullyVisible[0].id);
      return;
    }

    const visible = metrics.filter((m) => m.intersecting).sort((a, b) => a.rect.top - b.rect.top);
    if (visible.length) {
      this._setActive(visible[0].id);
      return;
    }

    const closest = metrics.slice().sort((a, b) => Math.abs(a.rect.top) - Math.abs(b.rect.top))[0];
    if (closest) this._setActive(closest.id);
  }

  _getVisibleSections() {
    // For each visible nav button, find a visible section element with matching ID
    return this.itemTargets
      .filter((btn) => this._isElementVisible(btn))
      .map((btn) => {
        const id = btn.dataset.scrollToSectionIdParam;
        if (!id) return null;

        // Find all elements with this ID and pick the first visible one
        const allElements = document.querySelectorAll(`[id="${id}"]`);
        const visibleEl = Array.from(allElements).find((el) => this._isElementVisible(el));

        return visibleEl ? { id, el: visibleEl, btn } : null;
      })
      .filter(Boolean);
  }

  _isElementVisible(el) {
    if (!el) return false;

    // Check if element or any parent has 'hidden' class
    let current = el;
    while (current) {
      if (current.classList && current.classList.contains('hidden')) return false;
      current = current.parentElement;
    }

    // Check computed style
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;

    // Check if element has any height (collapsed elements)
    const rect = el.getBoundingClientRect();
    if (rect.height === 0 && rect.width === 0) return false;

    return true;
  }

  _isAtBottomWithSections(sections) {
    const scroller = document.scrollingElement || document.documentElement;
    const viewportH = window.innerHeight || scroller.clientHeight;
    const epsilon = Math.max(2, Math.ceil(window.devicePixelRatio || 1));

    const maxScrollTop = scroller.scrollHeight - scroller.clientHeight;
    const scrollAtEnd = Math.ceil(scroller.scrollTop) >= Math.floor(maxScrollTop) - epsilon;

    const last = sections?.[sections.length - 1];
    if (!last?.el) return false;

    const rect = last.el.getBoundingClientRect();
    const lastTouchingBottom = rect.bottom <= viewportH + epsilon;

    return scrollAtEnd && lastTouchingBottom;
  }

  _setActive(id) {
    if (this._activeId === id) return;
    this._activeId = id;

    this.itemTargets.forEach((btn) => btn.classList.remove(this.activeClassValue));
    const targetBtn = this.itemTargets.find((btn) => btn.dataset.scrollToSectionIdParam === id);
    targetBtn?.classList.add(this.activeClassValue);
  }

  _flashAfterScroll(section) {
    this._waitForScrollIdle(() => this._flashSection(section), 120, 1500);
  }

  _waitForScrollIdle(cb, idleMs = 120, hardTimeout = 1500) {
    let idleTimer;
    const onIdle = () => {
      window.removeEventListener('scroll', onScroll, { passive: true });
      clearTimeout(hard);
      cb();
    };
    const onScroll = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(onIdle, idleMs);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    const hard = setTimeout(onIdle, hardTimeout);
    onScroll();
  }

  _flashSection(sectionEl) {
    const overlay = document.createElement('div');
    overlay.setAttribute('data-flash-overlay', 'true');
    overlay.className = [
      'pointer-events-none',
      'absolute',
      'inset-0',
      'z-[60]',
      'bg-black-100',
      'opacity-0',
      'transition-opacity',
      'duration-500',
      'ease-out',
      'rounded-16'
    ].join(' ');

    sectionEl.querySelectorAll('[data-flash-overlay="true"]').forEach((n) => n.remove());
    sectionEl.appendChild(overlay);

    requestAnimationFrame(() => {
      overlay.style.opacity = '0.9';
      setTimeout(() => {
        overlay.style.opacity = '0';
        overlay.addEventListener('transitionend', () => overlay.remove(), { once: true });
      }, 1000);
    });
  }

  _onClickOrFocus = (e) => {
    if (!e.target?.nodeType) return;
    const visibleSections = this._getVisibleSections();
    const section = visibleSections.find((s) => s?.el && s.el?.contains(e.target));
    if (section) {
      this._setActive(section.id);
    }
  }

  refreshSections() {
    if (this.observer) this.observer.disconnect();
    this.sections = this._buildSections();
    this.sections.forEach((s) => this.observer.observe(s.el));
    this._recomputeActive();
  }

  _buildSections() {
    return this.itemTargets
      .map((btn) => {
        const id = btn.dataset.scrollToSectionIdParam;
        const el = id ? document.getElementById(id) : null;
        return el ? { id, el, btn } : null;
      })
      .filter(Boolean);
  }
}
